import React, { forwardRef, useRef } from 'react';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import { getOlcButtonIcon } from './icons';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';

import './styles.scss';

const findCircularProgress = (children) => {
  let progress = null;

  React.Children.forEach(children, (child) => {
    if (progress || !React.isValidElement(child)) return;

    if (child.type === CircularProgress) {
      progress = child;
      return;
    }

    progress = findCircularProgress(child.props?.children);
  });

  return progress;
};

const OlcButton = forwardRef(
  (
    {
      variant = 'primary',
      size = 'small',
      className = '',
      children,
      icon,
      startIcon,
      endIcon,
      dropdown = false,
      expanded,
      iconOnly = false,
      ...buttonProps
    },
    ref
  ) => {
    const loadingIndicator = findCircularProgress(children);
    const lastNonLoadingChildren = useRef(null);

    if (!loadingIndicator) {
      lastNonLoadingChildren.current = children;
    }

    const displayedChildren = loadingIndicator ? lastNonLoadingChildren.current : children;
    const classes = [
      'olcButton',
      `olcButton--${variant}`,
      `olcButton--${size}`,
      loadingIndicator ? 'olcButton--loading' : '',
      iconOnly ? 'olcButton--iconOnly' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const leadingIcon =
      iconOnly && React.isValidElement(startIcon)
        ? startIcon
        : getOlcButtonIcon({
            children: displayedChildren,
            icon,
            startIcon,
            variant,
          });
    const isDropdown =
      dropdown || buttonProps['aria-haspopup'] === true || buttonProps['aria-haspopup'] === 'true';
    const isExpanded = expanded ?? buttonProps['aria-expanded'] === 'true';
    const trailingIcon = isDropdown ? (
      <OlcDropdownChevron open={isExpanded} tone={variant === 'primary' ? 'light' : 'dark'} />
    ) : (
      endIcon
    );

    return (
      <Button
        ref={ref}
        className={classes}
        size={size}
        disableElevation
        {...buttonProps}
        aria-busy={buttonProps['aria-busy'] ?? (loadingIndicator ? true : undefined)}
      >
        {leadingIcon && (
          <span className="olcButton__icon" aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        {!iconOnly && (
          <span className="olcButton__label">
            {loadingIndicator ? (
              <span className="olcButton__loadingPlaceholder" aria-hidden="true">
                {displayedChildren}
              </span>
            ) : (
              children
            )}
          </span>
        )}
        {trailingIcon && (
          <span className="olcButton__trailingIcon" aria-hidden="true">
            {trailingIcon}
          </span>
        )}
        {loadingIndicator && (
          <span className="olcButton__loadingIndicator" aria-hidden="true">
            {loadingIndicator}
          </span>
        )}
      </Button>
    );
  }
);

OlcButton.displayName = 'OlcButton';

export default OlcButton;
